import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { CurrencyIcon } from '@/components/ui/CurrencyIcon';
import { CircleFlag } from 'react-circle-flags';
import { SFIcon } from '@/components/ui/sf-icon';
import { sfIcons } from '@/lib/sf-icons';
import { ExternalAccount } from './hooks/useWallet';
import { semanticTokens } from '@packages/config/theme';
import { cn } from '@/lib/utils';

// Country flag based on currency (full circle coverage)
function getCurrencyFlag(currency?: string): string {
  switch (currency?.toUpperCase()) {
    case 'USD':
      return 'us';
    case 'EUR':
      return 'eu';
    case 'MXN':
      return 'mx';
    case 'BRL':
      return 'br';
    case 'GBP':
      return 'gb';
    default:
      return 'un'; // United Nations flag as fallback
  }
}

interface AccountSelectionCardProps {
  account: ExternalAccount;
  isSelected: boolean;
  onClick: () => void;
}

export function AccountSelectionCard({
  account,
  isSelected,
  onClick,
}: AccountSelectionCardProps) {
  const isActive = account.status === 'active';

  return (
    <Card
      className={cn(
        "cursor-pointer min-h-[56px]",
        semanticTokens.motion.standard,
        semanticTokens.focus.ring,
        "hover:shadow-md",
        isSelected 
          ? cn(semanticTokens.border.focus, 'ring-2 ring-blue-500 ring-offset-2') 
          : semanticTokens.border.default,
        !isActive && 'opacity-60',
        semanticTokens.bg.surface,
        semanticTokens.shadow.sm,
        semanticTokens.radius.lg,
        "focus-visible:outline-none"
      )}
      onClick={() => isActive && onClick()}
      role="button"
      tabIndex={0}
      aria-label={`Select ${account.bank_name || 'bank account'} ending in ${account.account_last4 || '****'}`}
      aria-pressed={isSelected}
      aria-disabled={!isActive}
      onKeyDown={(e) => {
        if ((e.key === 'Enter' || e.key === ' ') && isActive) {
          e.preventDefault();
          onClick();
        }
      }}
    >
      <CardContent className={cn(
        "p-4 flex justify-between items-center",
        semanticTokens.spacing.md
      )}>
        <div className={cn("flex items-center gap-3", semanticTokens.spacing.sm.replace('p-', 'gap-'))}>
          {/* Country Flag - Full circle coverage */}
          <div 
            className="w-10 h-10 lg:w-12 lg:h-12 shrink-0 overflow-hidden rounded-full"
            aria-hidden="true"
          >
            <CircleFlag 
              countryCode={getCurrencyFlag(account.currency)} 
              height="100%"
              width="100%"
              className="w-full h-full"
            />
          </div>

          {/* Account Info */}
          <div className="flex flex-col gap-1">
            <div className={cn(
              semanticTokens.typography.bodyLarge,
              semanticTokens.fg.default
            )}>
              {account.bank_name || 'Bank Account'}
            </div>
            <div className={cn(
              semanticTokens.typography.caption,
              semanticTokens.fg.muted
            )}>
              •••• {account.account_last4 || '****'}
              {account.currency && typeof account.currency === 'string' && (
                <span className="ml-2 inline-flex items-center gap-1">
                  • <CurrencyIcon symbol={account.currency} size="xs" />
                  {account.currency.toUpperCase()}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Status & Selection Indicator */}
        <div className="flex items-center gap-2">
          {account.status === 'active' ? (
            <Badge 
              variant="secondary" 
              className={cn(
                semanticTokens.positive.bg,
                semanticTokens.positive.fg,
                semanticTokens.typography.caption
              )}
            >
              Active
            </Badge>
          ) : (
            <Badge 
              variant="secondary" 
              className={cn(
                semanticTokens.warning.bg,
                semanticTokens.warning.fg,
                semanticTokens.typography.caption
              )}
            >
              {account.status}
            </Badge>
          )}
          {isSelected && (
            <SFIcon
              icon={sfIcons.success}
              className={cn("w-5 h-5", semanticTokens.positive.accent)}
              aria-hidden="true"
            />
          )}
        </div>
      </CardContent>
    </Card>
  );
}

